import React, { createContext, useContext, useEffect, useState } from 'react'
import { readViewportCssDimensions, subscribeViewportChange } from '@renderer/utils/viewportMetrics'

interface ScaleToFitProps {
  screenSize: {
    width: number
    height: number
  }
  children: React.ReactNode
  /** Background of the design canvas (sharp, inner stage). */
  backgroundColor?: string
  /**
   * Optional blurred duplicate of the stage for letterboxing (e.g. welcome: same layout as `children`, scaled + blurred).
   * Prefer this over `blurBackgroundSrc` when the filler should match the composed canvas.
   */
  backdropChildren?: React.ReactNode
  /** Optional full-bleed image behind letterboxing when `backdropChildren` is not set. */
  blurBackgroundSrc?: string
  /** Solid color for letterbox when `blurBackgroundSrc` is not set (falls back to black). */
  blurBackgroundColor?: string
}

/** Small extra scale on object-cover backdrop so blur doesn’t leave a faint gap at viewport edges. */
const LETTERBOX_BACKDROP_BLEED = 1.06

/** The CSS scale() applied by ScaleToFit. Children can counter-scale with `1 / scale`. */
const ScaleContext = createContext<number>(1)
export const useCanvasScale = (): number => useContext(ScaleContext)

function useWindowSize(): { cw: number; ch: number } {
  const [tick, setTick] = useState(0)

  useEffect(() => {
    const bump = (): void => setTick((t) => t + 1)
    return subscribeViewportChange(bump)
  }, [])

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- tick triggers recompute
  void tick

  if (typeof window === 'undefined') {
    return { cw: 1080, ch: 1920 }
  }
  const { width: cw, height: ch } = readViewportCssDimensions()
  return { cw, ch }
}

/**
 * Fit the design canvas into the viewport with uniform scale (object-contain).
 * Letterbox: `backdropChildren` (blurred stage duplicate at object-cover scale), else blurred theme image, else solid color.
 */
const ScaleToFit: React.FC<ScaleToFitProps> = ({
  screenSize,
  children,
  backgroundColor = 'white',
  backdropChildren,
  blurBackgroundSrc,
  blurBackgroundColor
}) => {
  const { cw, ch } = useWindowSize()
  const { width: pw, height: ph } = screenSize
  const scale =
    pw > 0 && ph > 0 && cw > 0 && ch > 0 ? Math.min(cw / pw, ch / ph) : 1
  /** Uniform scale so design canvas covers the viewport (for blurred backdrop only; sharp layer stays contain). */
  const backdropCoverScale =
    pw > 0 && ph > 0 && cw > 0 && ch > 0 ? Math.max(cw / pw, ch / ph) : 1

  const letterboxFill = blurBackgroundColor ?? backgroundColor ?? '#000000'

  return (
    <ScaleContext.Provider value={scale}>
      <div className="relative flex h-full min-h-0 w-full min-w-0 flex-1 items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 z-0 overflow-hidden"
          style={{ backgroundColor: letterboxFill }}
          aria-hidden
        >
          {backdropChildren ? (
            <div
              className="pointer-events-none absolute left-1/2 top-1/2 select-none"
              style={{
                width: `${pw}px`,
                height: `${ph}px`,
                transform: `translate(-50%, -50%) scale(${backdropCoverScale * LETTERBOX_BACKDROP_BLEED})`,
                transformOrigin: 'center center',
                filter: 'blur(32px)',
                willChange: 'transform, filter'
              }}
            >
              {backdropChildren}
            </div>
          ) : blurBackgroundSrc ? (
            <img
              src={blurBackgroundSrc}
              alt=""
              className="absolute left-1/2 top-1/2 min-h-[120%] min-w-[120%] max-w-none object-cover"
              style={{
                transform: 'translate(-50%, -50%) scale(1.12)',
                filter: 'blur(32px)'
              }}
            />
          ) : null}
        </div>
        <div
          className="relative z-10 shrink-0 overflow-hidden"
          style={{
            width: `${pw}px`,
            height: `${ph}px`,
            backgroundColor,
            transform: `scale(${scale})`,
            transformOrigin: 'center center'
          }}
        >
          {children}
        </div>
      </div>
    </ScaleContext.Provider>
  )
}

export default ScaleToFit
